import { useMemo, useRef, useState } from "react";
import {
  ApiError,
  type BulkAddItemsReport,
  type ExtractedDocumentImage,
  type GeneratedDimension,
  type GeneratedNodeAttribute,
  type InstanceCustomisationView,
} from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { Icon } from "../components/Icon";
import { Modal } from "../components/chrome/Modal";
import { DEFAULT_SEGMENT_NOUN, article, type SegmentNoun } from "@keydials/dial-workspace";

const ACCEPT =
  ".pdf,.docx,.xlsx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/markdown";

type Step = "pick" | "analysing" | "review";

/** Appends a "Source: <file> — <where>" provenance line to a candidate's description, within the
 * 2000-char description cap (the server clips too; this keeps what the user sees accurate). */
function withSource(description: string | null | undefined, fileName: string, sourceReference: string | null | undefined): string {
  const source = `Source: ${fileName}${sourceReference ? ` — ${sourceReference}` : ""}`;
  const combined = description ? `${description}\n\n${source}` : source;
  return combined.length <= 2000 ? combined : combined.slice(0, 2000);
}

/** One photo the model matched to this row, editable before confirming. `imageIndex` resolves against
 * the analysis result's `images` list (looked up once, by the modal, for the actual bytes). */
type RowImage = {
  imageIndex: number;
  category: "Found" | "Resolution" | "Uncategorized";
  /** Unticked photos are dropped — the row is still created, just without that photo attached. */
  keep: boolean;
};

const CATEGORY_OPTIONS: { id: RowImage["category"]; label: string }[] = [
  { id: "Found", label: "Found" },
  { id: "Resolution", label: "Resolution" },
  { id: "Uncategorized", label: "Other" },
];

/** One reviewable candidate row. `segmentKey` null = unassigned (excluded until the user places it). */
type Row = {
  id: number;
  include: boolean;
  name: string;
  description: string;
  guidance: string;
  segmentKey: string | null;
  confidence: string | null;
  sourceReference: string | null;
  attributes: Record<string, string>;
  /** The inspector's commentary from the document — posted to the item's discussion on confirm. */
  comment: string;
  /** Status to set on the created item ("" = the dial's initial status). */
  statusKey: string;
  expanded: boolean;
  /** Photos the model matched to this item, if any (KEY-237). */
  images: RowImage[];
};

const inputStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 13.5,
  padding: "6px 9px",
  border: "var(--border-w) solid var(--border)",
  borderRadius: "var(--radius-sm)",
  background: "var(--surface)",
  color: "var(--text)",
  width: "100%",
  boxSizing: "border-box",
};

function ConfidenceBadge({ value }: { value: string | null }) {
  if (!value) return null;
  const colour = value === "high" ? "var(--st-go, #16a34a)" : value === "medium" ? "var(--st-amber, #b45309)" : "var(--text-faint)";
  return (
    <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: colour, whiteSpace: "nowrap" }}>
      {value}
    </span>
  );
}

/**
 * The AI document → items import flow (upload → analyse → review mapping → confirm). The review step
 * lets the user include/exclude each candidate, edit its wording and attribute values, and re-target
 * the segment the AI suggested; confirming creates the accepted items additively via bulk-add.
 */
export function DocumentItemImport({
  instanceId,
  view,
  itemAttributes,
  statusDimension = null,
  onClose,
  onImported,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  instanceId: string;
  view: InstanceCustomisationView;
  /** The definition's declared Item-target attributes (Text/Date) offered as editable columns. */
  itemAttributes: GeneratedNodeAttribute[];
  /** The dial's primary item-status dimension (itemised dials) — enables per-item status suggestions. */
  statusDimension?: GeneratedDimension | null;
  onClose: () => void;
  onImported: (report: BulkAddItemsReport) => void | Promise<void>;
  /** This dial's word for an action point (KEY-539); omitted means the platform's word. */
  noun?: SegmentNoun;
}) {
  const [step, setStep] = useState<Step>("pick");
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [truncated, setTruncated] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [rows, setRows] = useState<Row[]>([]);
  const [images, setImages] = useState<ExtractedDocumentImage[]>([]);
  const [sourceJobId, setSourceJobId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const imagesByIndex = useMemo(() => new Map(images.map((img) => [img.index, img])), [images]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Closing mid-analysis abandons the poller: the job finishes (or fails) harmlessly server-side.
  const cancelledRef = useRef(false);

  const segments = useMemo(
    () =>
      view.sections.map((section) => ({
        section,
        options: section.segments.map((segment) => ({ key: segment.key, label: `${segment.label} · ${segment.name}` })),
      })),
    [view.sections],
  );

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setFileName(file.name);
    setStep("analysing");
    setElapsed(0);
    try {
      const result = await api.analyzeInstanceDocument(getAccessToken() ?? "", instanceId, file, {
        onPoll: (ms) => setElapsed(Math.round(ms / 1000)),
      });
      if (cancelledRef.current) return;
      setTruncated(result.truncated);
      setImages(result.images ?? []);
      setSourceJobId(result.jobId);
      setRows(
        result.items.map((item, i) => ({
          id: i,
          include: item.suggestedSegmentKey != null,
          name: item.name,
          // Provenance rides in the description (visible + editable in the row's details before
          // confirming) so each created item stays traceable to the report it came from.
          description: withSource(item.description, file.name, item.sourceReference),
          guidance: item.guidance ?? "",
          segmentKey: item.suggestedSegmentKey ?? null,
          confidence: item.confidence ?? null,
          sourceReference: item.sourceReference ?? null,
          attributes: Object.fromEntries((item.attributes ?? []).map((a) => [a.key, a.value])),
          comment: item.inspectorComment ?? "",
          statusKey: item.suggestedStatusKey ?? "",
          expanded: false,
          images: (item.images ?? []).map((img) => ({
            imageIndex: img.imageIndex,
            category: (["Found", "Resolution", "Uncategorized"] as const).includes(img.category as RowImage["category"])
              ? (img.category as RowImage["category"])
              : "Uncategorized",
            keep: true,
          })),
        })),
      );
      setStep("review");
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof ApiError ? err.message : "Analysing the document failed. Please try again.");
      setStep("pick");
    }
  }

  const patchRow = (id: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const patchRowImage = (rowId: number, imageIndex: number, patch: Partial<RowImage>) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id !== rowId
          ? r
          : { ...r, images: r.images.map((img) => (img.imageIndex === imageIndex ? { ...img, ...patch } : img)) },
      ),
    );

  const included = rows.filter((r) => r.include && r.segmentKey && r.name.trim());

  // Advisory cap check (the server report stays authoritative): existing items per segment + what this
  // batch sends there, against the envelope's per-segment cap.
  const capWarnings = useMemo(() => {
    const cap = view.headroom.maxItemsPerSegment;
    if (cap == null) return [];
    const warnings: string[] = [];
    for (const { section, options } of segments) {
      for (const option of options) {
        const existing = section.segments.find((s) => s.key === option.key)?.items.length ?? 0;
        const incoming = included.filter((r) => r.segmentKey === option.key).length;
        if (incoming > 0 && existing + incoming > cap) {
          const room = Math.max(0, cap - existing);
          warnings.push(`${option.label}: only ${room} more item${room === 1 ? "" : "s"} allowed — ${existing + incoming - cap} will be skipped.`);
        }
      }
    }
    return warnings;
  }, [segments, included, view.headroom.maxItemsPerSegment]);

  async function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      const report = await api.bulkAddInstanceItems(
        getAccessToken() ?? "",
        instanceId,
        included.map((r) => ({
          segmentKey: r.segmentKey!,
          name: r.name.trim(),
          description: r.description.trim() || null,
          guidance: r.guidance.trim() || null,
          attributes: Object.entries(r.attributes)
            .filter(([, value]) => value.trim())
            .map(([key, value]) => ({ key, value: value.trim() })),
          comment: r.comment.trim() || null,
          statusKey: (statusDimension && r.statusKey) || null,
          images: r.images.filter((img) => img.keep).map((img) => ({ imageIndex: img.imageIndex, category: img.category })),
        })),
        statusDimension?.key,
        sourceJobId ?? undefined,
      );
      await onImported(report);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? "You no longer have customisation rights on this dial — reload and try again."
          : err instanceof ApiError
            ? err.message
            : "Adding the items failed. Please try again.",
      );
      setSubmitting(false);
    }
  }

  const close = () => {
    cancelledRef.current = true;
    onClose();
  };

  return (
    <Modal
      title="Import items from a document"
      subtitle={step === "review" ? `${fileName} — review the proposed items, then add the ones you want.` : undefined}
      onClose={close}
      width={760}
    >
      {step === "pick" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6 }}>
            Upload a report, checklist or schedule (PDF, Word, Excel, or plain text — up to 10 MB). AI reads it
            against this dial's structure and proposes items mapped to the right {noun.many}; nothing is added
            until you review and confirm.
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="kd-focusable"
            style={{
              font: "inherit",
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px",
              borderRadius: "var(--radius-sm)",
              border: "var(--border-w) solid var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            <Icon name="upload" size={15} />
            Choose a document…
          </button>
          <input ref={fileInputRef} type="file" accept={ACCEPT} onChange={onFilePicked} style={{ display: "none" }} />
          {error && <p style={{ margin: 0, color: "var(--st-overdue, #dc2626)", fontSize: 13 }}>{error}</p>}
        </div>
      )}

      {step === "analysing" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "28px 0" }}>
          <Icon name="sparkles" size={26} style={{ color: "var(--brand-600, #4338ca)" }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>Analysing {fileName}…</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Reading the document against this dial's structure. This can take a minute or two.
            {elapsed > 0 ? ` (${elapsed}s)` : ""}
          </div>
          <button
            type="button"
            onClick={close}
            style={{ font: "inherit", fontSize: 13, fontWeight: 600, padding: "6px 12px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)", cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
      )}

      {step === "review" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {truncated && (
            <div style={{ padding: "8px 12px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--st-amber, #b45309)", background: "var(--surface-2)", fontSize: 13, color: "var(--text-muted)" }}>
              This document is long — only the first part was analysed, so some items may be missing.
            </div>
          )}

          {rows.length === 0 && (
            <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-faint)", fontSize: 14 }}>
              No candidate items were found in {fileName}.
            </div>
          )}

          {rows.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 12px", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={included.length > 0 && rows.every((r) => r.include || r.segmentKey == null)}
                onChange={(e) =>
                  setRows((prev) => prev.map((r) => (r.segmentKey == null ? r : { ...r, include: e.target.checked })))
                }
                aria-label="Include or exclude all items"
                title="Include or exclude all items"
                style={{ flex: "none", width: 16, height: 16 }}
              />
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)" }}>
                Tick the items to add — {included.length} of {rows.length} selected
              </span>
            </div>
          )}

          {rows.map((row) => {
            const unassigned = row.segmentKey == null;
            return (
              <div
                key={row.id}
                style={{
                  border: "var(--border-w) solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  opacity: row.include ? 1 : 0.55,
                }}
              >
                {/* userSelect none: a fast double-click on the row controls otherwise text-selects the
                    surrounding rows, which reads as items being "selected". */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, userSelect: "none" }}>
                  <input
                    type="checkbox"
                    checked={row.include}
                    disabled={unassigned}
                    aria-label={unassigned ? `Assign ${article(noun.one)} ${noun.one} first` : "Include this item"}
                    title={unassigned ? `Assign ${article(noun.one)} ${noun.one} first` : row.include ? "Ticked — this item will be added" : "Unticked — this item won't be added"}
                    onChange={(e) => patchRow(row.id, { include: e.target.checked })}
                    style={{ flex: "none", width: 16, height: 16 }}
                  />
                  <input
                    value={row.name}
                    onChange={(e) => patchRow(row.id, { name: e.target.value })}
                    style={{ ...inputStyle, flex: 2, minWidth: 160, fontWeight: 600 }}
                    aria-label="Item name"
                  />
                  <select
                    value={row.segmentKey ?? ""}
                    onChange={(e) =>
                      patchRow(row.id, {
                        segmentKey: e.target.value || null,
                        // Auto-include when the user places an unassigned candidate.
                        include: e.target.value ? row.include || unassigned : false,
                      })
                    }
                    style={{ ...inputStyle, flex: 1, minWidth: 170, width: "auto" }}
                    aria-label={`Target ${noun.one}`}
                  >
                    <option value="">— unassigned —</option>
                    {segments.map(({ section, options }) => (
                      <optgroup key={section.key} label={section.label}>
                        {options.map((o) => (
                          <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => patchRow(row.id, { expanded: !row.expanded })}
                    // Swallow the mousedown so a double-click can't start a text selection across rows.
                    onMouseDown={(e) => e.preventDefault()}
                    title={row.expanded ? "Close details" : "Edit details"}
                    aria-label={row.expanded ? "Close details" : "Edit details"}
                    style={{ font: "inherit", flex: "none", border: "none", background: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4 }}
                  >
                    <Icon name={row.expanded ? "x" : "pencil"} size={14} />
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, paddingLeft: 26, fontSize: 12.5, color: "var(--text-faint)", userSelect: "none" }}>
                  <ConfidenceBadge value={row.confidence} />
                  {statusDimension && row.statusKey && (
                    <span title="Suggested from the document — change it in the item's details">
                      Status: {statusDimension.palette.statuses.find((s) => s.key === row.statusKey)?.name ?? row.statusKey}
                    </span>
                  )}
                  {row.comment && (
                    <span title="The inspector's commentary will be posted to the item's discussion">
                      <Icon name="chat" size={12} style={{ verticalAlign: -2, marginRight: 3 }} />Inspector comment
                    </span>
                  )}
                  {row.sourceReference && <span>From: {row.sourceReference}</span>}
                  {unassigned && <span style={{ color: "var(--st-amber, #b45309)", fontWeight: 700 }}>Assign {article(noun.one)} {noun.one} to include this item</span>}
                </div>

                {row.images.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingLeft: 26 }}>
                    {row.images.map((img) => {
                      const source = imagesByIndex.get(img.imageIndex);
                      if (!source) return null;
                      return (
                        <div key={img.imageIndex} style={{ display: "flex", flexDirection: "column", gap: 4, width: 64 }}>
                          <div style={{ position: "relative", width: 64, height: 64 }}>
                            <img
                              src={`data:${source.mediaType};base64,${source.base64}`}
                              alt=""
                              style={{
                                width: 64, height: 64, objectFit: "cover", borderRadius: "var(--radius-sm)",
                                border: "var(--border-w) solid var(--border)", opacity: img.keep ? 1 : 0.35,
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => patchRowImage(row.id, img.imageIndex, { keep: !img.keep })}
                              title={img.keep ? "Remove this photo" : "Keep this photo"}
                              aria-label={img.keep ? "Remove this photo" : "Keep this photo"}
                              style={{
                                position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: 99,
                                display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                                border: "var(--border-w) solid var(--border)", cursor: "pointer",
                                background: img.keep ? "var(--surface)" : "var(--st-overdue, #dc2626)",
                                color: img.keep ? "var(--text-muted)" : "#fff",
                              }}
                            >
                              <Icon name={img.keep ? "x" : "plus"} size={10} />
                            </button>
                          </div>
                          <select
                            value={img.category}
                            disabled={!img.keep}
                            onChange={(e) => patchRowImage(row.id, img.imageIndex, { category: e.target.value as RowImage["category"] })}
                            aria-label="Photo category"
                            style={{ ...inputStyle, fontSize: 10.5, padding: "3px 4px", width: 64 }}
                          >
                            {CATEGORY_OPTIONS.map((o) => (
                              <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}

                {row.expanded && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 26 }}>
                    {statusDimension && (
                      <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
                        Initial status ({statusDimension.label})
                        <select
                          value={row.statusKey}
                          onChange={(e) => patchRow(row.id, { statusKey: e.target.value })}
                          style={{ ...inputStyle, marginTop: 4 }}
                        >
                          <option value="">Dial default</option>
                          {statusDimension.palette.statuses.map((s) => (
                            <option key={s.key} value={s.key}>{s.name}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
                      Inspector comment (posted to the item's discussion)
                      <textarea
                        value={row.comment}
                        onChange={(e) => patchRow(row.id, { comment: e.target.value })}
                        rows={2}
                        placeholder="No commentary found in the document"
                        style={{ ...inputStyle, marginTop: 4, resize: "vertical" }}
                      />
                    </label>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
                      Description
                      <textarea
                        value={row.description}
                        onChange={(e) => patchRow(row.id, { description: e.target.value })}
                        rows={2}
                        style={{ ...inputStyle, marginTop: 4, resize: "vertical" }}
                      />
                    </label>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
                      Guidance
                      <textarea
                        value={row.guidance}
                        onChange={(e) => patchRow(row.id, { guidance: e.target.value })}
                        rows={2}
                        style={{ ...inputStyle, marginTop: 4, resize: "vertical" }}
                      />
                    </label>
                    {itemAttributes.map((attribute) => (
                      <label key={attribute.key} style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
                        {attribute.label}
                        <input
                          type={attribute.type === "Date" ? "date" : "text"}
                          value={row.attributes[attribute.key] ?? ""}
                          onChange={(e) =>
                            patchRow(row.id, { attributes: { ...row.attributes, [attribute.key]: e.target.value } })
                          }
                          style={{ ...inputStyle, marginTop: 4 }}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {capWarnings.map((w) => (
            <div key={w} style={{ fontSize: 12.5, color: "var(--st-amber, #b45309)", fontWeight: 600 }}>{w}</div>
          ))}

          {error && <p style={{ margin: 0, color: "var(--st-overdue, #dc2626)", fontSize: 13 }}>{error}</p>}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              style={{ font: "inherit", fontSize: 13.5, fontWeight: 600, padding: "8px 14px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={submitting || included.length === 0}
              className="kd-focusable"
              style={{
                font: "inherit",
                fontSize: 13.5,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: "var(--radius-sm)",
                border: "none",
                background: included.length === 0 || submitting ? "var(--surface-2)" : "var(--brand-600, #4338ca)",
                color: included.length === 0 || submitting ? "var(--text-faint)" : "#fff",
                cursor: included.length === 0 || submitting ? "default" : "pointer",
              }}
            >
              <Icon name="plus" size={14} />
              {submitting ? "Adding…" : `Add ${included.length} item${included.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
